
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight, Crown, Trophy, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MentorshipOfferingCard } from "@/components/mentor/MentorshipOffering";
import { MentorshipOffering } from "@/services/mentorOfferingsService";
import { FeaturedItem } from "@/services/featuredItemsService";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

interface MentorshipsSectionProps {
  mentorships: MentorshipOffering[] | undefined;
  featuredItems?: FeaturedItem[];
}

export const MentorshipsSection: React.FC<MentorshipsSectionProps> = ({ mentorships, featuredItems }) => {
  const navigate = useNavigate();

  // Função para obter ícone da posição
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Crown className="h-4 w-4 text-yellow-400" />;
      case 2:
        return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3:
        return <Star className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  // Função para obter classe da borda do destaque
  const getFeaturedBorderClass = (position: number) => {
    switch (position) {
      case 1:
        return "border-yellow-400 shadow-yellow-400/20";
      case 2:
        return "border-gray-400 shadow-gray-400/20";
      case 3:
        return "border-amber-600 shadow-amber-600/20";
      default:
        return "border-[#223342]";
    }
  };
  
  return (
    <section className="py-16 md:py-24 px-4 relative">
      <div className="absolute top-1/4 left-1/3 w-64 h-64 bg-[#00B6F0] opacity-10 blur-[100px] rounded-full -z-10"></div>
      <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-[#8B5CF6] opacity-10 blur-[120px] rounded-full -z-10"></div>
      
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-12">
          <BookOpen className="h-6 w-6 text-[#00B6F0]" />
          <h2 className="text-3xl md:text-4xl font-bold text-white">Mentorias Disponíveis</h2>
        </div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" 
          variants={containerVariants} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true, amount: 0.1 }}
        >
          {mentorships ? (
            mentorships.map(mentorship => {
              const featuredItem = featuredItems?.find(item => item.item_id === mentorship.id);
              const position = featuredItem?.position || 0;
              const isFeatured = !!featuredItem;
              
              return (
                <motion.div key={mentorship.id} variants={itemVariants} className="relative">
                  {/* Badge de posição para mentorias em destaque */}
                  {isFeatured && (
                    <div className="absolute -top-2 -right-2 z-30 bg-gradient-to-r from-[#00B6F0] to-[#0099CC] rounded-full p-2 shadow-lg">
                      {getPositionIcon(position)}
                    </div>
                  )}
                  
                  <div className={isFeatured ? `border-2 ${getFeaturedBorderClass(position)} rounded-xl shadow-lg` : ''}>
                    <MentorshipOfferingCard 
                      id={mentorship.id} 
                      title={mentorship.title} 
                      description={mentorship.description} 
                      price={mentorship.price_cents} 
                      duration={mentorship.duration_minutes} 
                      game={mentorship.game} 
                      coverImageUrl={mentorship.cover_image_url} 
                      isActive={mentorship.is_active} 
                      isGroup={mentorship.is_group} 
                      maxParticipants={mentorship.max_participants} 
                      mentorName={mentorship.mentor_name} 
                      mentorImageUrl={mentorship.mentor_image_url} 
                      mentorId={mentorship.mentor_id} 
                      onCardClick={() => navigate(`/mentores/${mentorship.mentor_id}`)} 
                    />
                  </div>
                </motion.div>
              );
            })
          ) : (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="w-full h-96 bg-[#181D24] rounded-xl animate-pulse"></div>
            ))
          )}
        </motion.div>

        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate("/mentorias")} 
            variant="outline" 
            size="lg" 
            className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
          >
            Ver Todas as Mentorias
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};
